<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
    :fullscreen="$vuetify.breakpoint.mobile"
  >
    <template #activator="{ on: d }">
      <template v-if="$scopedSlots['activator']">
        <!-- custom activator -->
        <slot
          name="activator"
          v-bind="{
            on: d,
            loading: loadingTotal,
            total: total
          }"
        />
      </template>
      <template v-else>
        <!-- default activator -->
        <v-tooltip bottom>
          <template #activator="{ on: tooltip }">
            <v-badge
              color="primary"
              :content="total"
              overlap
              :value="total > 0 && !loadingTotal"
            >
              <v-btn icon :loading="loadingTotal" v-on="{ ...d, ...tooltip }">
                <v-icon small>$freshvuenotes-show-notes</v-icon>
              </v-btn>
            </v-badge>
          </template>
          <span>{{ tooltipText }}</span>
        </v-tooltip>
      </template>
    </template>

    <card-base
      dialog
      draggable
      title="Notizen"
      :loading="$apollo.queries.notes.loading"
      :disabled="$apollo.queries.notes.loading"
      @close="dialog = false"
    >
      <template #toolbarIconActions>
        <note-save-dialog
          :noteable-id="noteableId"
          :noteable-type="noteableType"
          :enable-notifications="enableNotifications"
          @submit:success="refetch"
        >
          <template #activator="{ on }">
            <button-tooltip
              :tooltip-text="$t('fresh-vue-notes.misc.add')"
              icon="$freshvuenotes-add"
              v-on="on"
            />
          </template>
        </note-save-dialog>
      </template>

      <template v-if="notes.length > 0" #expandPrepend>
        <!-- show only my notes -->
        <v-chip
          filter
          small
          outlined
          :input-value="filter.user"
          class="mr-2"
          @click="filter.user = filter.user ? null : authUser.user_id"
        >
          <v-icon small left>$freshvuenotes-filter-user</v-icon>
          {{ $t('fresh-vue-notes.filters.only-my-notes') }}
        </v-chip>

        <!-- show only notes with files -->
        <v-chip
          filter
          small
          outlined
          :input-value="filter.files"
          @click="filter.files = !filter.files"
        >
          <v-icon small left>$freshvuenotes-filter-file</v-icon>
          {{ $t('fresh-vue-notes.filters.only-with-files') }}
        </v-chip>

        <!-- severity -->
        <v-chip-group v-model="filter.severity" column>
          <v-chip
            v-for="item in ['NONE', 'INFO', 'SUCCESS', 'WARNING', 'ERROR']"
            :key="item"
            filter
            small
            outlined
            :value="item"
          >
            <note-severity-icon :severity="item" />
            <span class="ml-2">{{
              $t(`fresh-vue-notes.severity.${item}`)
            }}</span>
          </v-chip>
        </v-chip-group>
      </template>

      <notes-list
        :notes="internalItems"
        :loading="$apollo.queries.notes.loading"
        @reload="refetch"
      />
    </card-base>
  </v-dialog>
</template>

<script>
import gql from 'graphql-tag'
import { mapGetters } from 'vuex'

import { AUDIT as FRAGMENT_AUDIT } from '@freshsolutions/fresh-vue-audits/src/graphql/fragments.js'
import { FILE as FRAGMENT_FILE } from '@freshsolutions/fresh-vue-files/src/graphql/fragments.js'

import CardBase from '@freshsolutions/fresh-vue-cards/src/components/CardBase.vue' // Just import the component we need. Otherwise it won't work.
import { ButtonTooltip } from '@freshsolutions/fresh-vue'
import NoteSaveDialog from './NoteSaveDialog'
import NoteSeverityIcon from './NoteSeverityIcon'
import NotesList from './NotesList'

export default {
  name: 'NotesDialog',

  components: {
    CardBase,
    ButtonTooltip,
    NoteSaveDialog,
    NoteSeverityIcon,
    NotesList
  },

  props: {
    noteableId: {
      type: Number,
      required: true
    },

    noteableType: {
      type: String,
      required: true
    },

    tooltipText: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-notes.actions.show-notes')
      }
    },

    enableNotifications: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  apollo: {
    notesCount: {
      query: gql`
        query notes($noteable_id: Int!, $noteable_type: String!) {
          notesCount(noteable_id: $noteable_id, noteable_type: $noteable_type)
        }
      `,
      variables() {
        return {
          noteable_id: this.noteableId,
          noteable_type: this.noteableType
        }
      },
      fetchPolicy: 'network-only'
    },

    notes: {
      query: gql`
        query notes($noteable_id: Int!, $noteable_type: String!) {
          notes(
            noteable_id: $noteable_id
            noteable_type: $noteable_type
            orderBy: [{ column: CREATED_AT, order: DESC }]
          ) {
            id

            private
            severity
            title
            description

            audits {
              ...Audit
            }

            files {
              ...File
              url_show
              url_download
            }

            created_at
            created_by {
              id
              name
            }
            updated_at
          }
        }
        ${FRAGMENT_AUDIT}
        ${FRAGMENT_FILE}
      `,
      variables() {
        return {
          noteable_id: this.noteableId,
          noteable_type: this.noteableType
        }
      },
      skip() {
        return !this.dialog
      },
      fetchPolicy: 'network-only'
    }
  },

  data() {
    return {
      notes: [],
      notesCount: null,
      dialog: false,

      filter: {
        user: false,
        files: false,
        severity: null
      }
    }
  },

  computed: {
    ...mapGetters('auth', {
      authUser: 'user'
    }),

    total() {
      return this.notesCount
    },

    loadingTotal() {
      return this.$apollo.queries.notesCount.loading
    },

    internalItems() {
      let _return = this.notes

      if (this.filter) {
        const filter = this.filter

        if (filter.user) {
          _return = _return.filter((item) => item.created_by.id == filter.user)
        }

        if (filter.files) {
          _return = _return.filter((item) => item.files.length > 0)
        }

        if (filter.severity) {
          _return = _return.filter((item) => item.severity === filter.severity)
        }
      }

      return _return
    }
  },

  methods: {
    refetch() {
      this.$apollo.queries.notesCount.refetch()
      this.$apollo.queries.notes.refetch()
    }
  }
}
</script>
